import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { User, ShoppingCart, Star, Facebook, Instagram } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';

interface RestaurantDetails {
  id: string;
  name: string;
  ordering_page_url?: string;
  review_page_url?: string;
  google_review_url?: string;
  facebook_url?: string;
  instagram_url?: string;
}

interface PersonalizationOptionsProps {
  onInsertPersonalization: (value: string) => void;
  restaurants?: RestaurantDetails[];
  messageContent?: string;
  selectedRestaurantId?: string;
  onRestaurantChange?: (restaurantId: string) => void;
  allowDynamicVariables?: boolean;
}

const DYNAMIC_RESTAURANT_ID = 'dynamic_variables';

const DYNAMIC_VARIABLES: Record<string, string> = {
  ordering_page_url: '{preferred restaurant ordering url}',
  review_page_url: '{preferred restaurant review url}',
  google_review_url: '{preferred restaurant google review url}',
  facebook_url: '{preferred restaurant facebook url}',
  instagram_url: '{preferred restaurant instagram url}'
};

const PersonalizationOptions: React.FC<PersonalizationOptionsProps> = ({
  onInsertPersonalization,
  restaurants = [],
  messageContent = '',
  selectedRestaurantId: externalSelectedRestaurantId,
  onRestaurantChange,
  allowDynamicVariables = false
}) => {
  const { toast } = useToast();
  
  // Use external state if provided, otherwise use internal state
  const [internalSelectedRestaurantId, setInternalSelectedRestaurantId] = useState<string>(
    restaurants.length === 1 ? String(restaurants[0].id) : ''
  );
  
  const selectedRestaurantId = externalSelectedRestaurantId ?? internalSelectedRestaurantId;
  const setSelectedRestaurantId = onRestaurantChange ?? setInternalSelectedRestaurantId;

  const isVariableActive = (variable: string) => {
    return messageContent.includes(variable);
  };

  const isUrlActive = (urlField: keyof RestaurantDetails) => {
    if (!restaurants || restaurants.length === 0) return false;

    // Check if dynamic variables mode is selected
    if (selectedRestaurantId === DYNAMIC_RESTAURANT_ID) {
      const dynamicVariable = DYNAMIC_VARIABLES[urlField];
      return dynamicVariable ? messageContent.includes(dynamicVariable) : false;
    }

    let restaurant: RestaurantDetails | undefined;

    if (restaurants.length === 1) {
      restaurant = restaurants[0];
    } else if (selectedRestaurantId) {
      restaurant = restaurants.find(r => r.id === selectedRestaurantId);
    }

    if (!restaurant) return false;

    const url = restaurant[urlField];
    return url ? messageContent.includes(url as string) : false;
  };

  /**
   * Check if URL is available for insertion
   * In dynamic mode: always true (variables always available)
   * In static mode: check if restaurant has the URL
   */
  const isUrlAvailable = (urlField: keyof RestaurantDetails): boolean => {
    // Dynamic mode: Variables are always available
    if (selectedRestaurantId === DYNAMIC_RESTAURANT_ID) {
      return true;
    }

    // No restaurants: URLs not available
    if (!restaurants || restaurants.length === 0) {
      return false;
    }

    // Get selected restaurant
    let restaurant: RestaurantDetails | undefined;

    if (restaurants.length === 1) {
      restaurant = restaurants[0];
    } else if (selectedRestaurantId) {
      restaurant = restaurants.find(r => r.id === selectedRestaurantId);
    }

    if (!restaurant) {
      return false;
    }

    // Check if URL exists and is not empty
    const url = restaurant[urlField];
    return !!url && (url as string).trim() !== '';
  };

  const handleUrlInsert = (urlField: keyof RestaurantDetails) => {
    if (!restaurants || restaurants.length === 0) {
      toast({
        title: 'No restaurants available',
        description: 'You need access to at least one restaurant to insert URLs.',
        variant: 'destructive'
      });
      return;
    }

    // Check if dynamic variables mode is selected
    if (selectedRestaurantId === DYNAMIC_RESTAURANT_ID) {
      const dynamicVariable = DYNAMIC_VARIABLES[urlField];
      if (dynamicVariable) {
        onInsertPersonalization(dynamicVariable);
      }
      return;
    }

    let restaurant: RestaurantDetails | undefined;
    
    if (restaurants.length === 1) {
      restaurant = restaurants[0];
    } else {
      if (!selectedRestaurantId) {
        toast({
          title: 'Select a restaurant',
          description: 'Please select a restaurant to use its URLs.',
          variant: 'destructive'
        });
        return;
      }
      restaurant = restaurants.find(r => r.id === selectedRestaurantId);
    }

    if (!restaurant) {
      toast({
        title: 'Restaurant not found',
        description: 'Could not find the selected restaurant.',
        variant: 'destructive'
      });
      return;
    }

    const url = restaurant[urlField];
    if (!url) {
      toast({
        title: 'URL not set',
        description: `${urlField.toString().replace(/_/g, ' ')} is not set for this restaurant.`,
        variant: 'destructive'
      });
      return;
    }
    
    onInsertPersonalization(url as string);
  };

  const showRestaurantSelector = restaurants.length > 1;

  return (
    <div className="space-y-2">
      <Label>Personalisation Options</Label>
      
      {showRestaurantSelector && (
        <div className="mb-3">
          <Label className="text-sm text-muted-foreground">
            Select a restaurant to use its URLs:
          </Label>
          <Select value={selectedRestaurantId} onValueChange={setSelectedRestaurantId}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a restaurant" />
            </SelectTrigger>
            <SelectContent>
              {allowDynamicVariables && (
                <SelectItem key={DYNAMIC_RESTAURANT_ID} value={DYNAMIC_RESTAURANT_ID}>
                  Dynamic Variables
                </SelectItem>
              )}
              {restaurants.map(restaurant => (
                <SelectItem key={restaurant.id} value={String(restaurant.id)}>
                  {restaurant.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <Button 
          size="sm" 
          variant={isVariableActive('{first name}') ? "default" : "outline"}
          onClick={() => onInsertPersonalization('{first name}')}
          className={cn(
            "flex items-center",
            isVariableActive('{first name}') && "bg-primary text-primary-foreground [&_svg]:text-white [&_svg]:stroke-white"
          )}
        >
          <User className="mr-2 h-4 w-4" />
          First Name
        </Button>
        
        <Button
          size="sm"
          variant={isUrlActive('ordering_page_url') ? "default" : "outline"}
          onClick={() => handleUrlInsert('ordering_page_url')}
          disabled={!isUrlAvailable('ordering_page_url')}
          className={cn(
            "flex items-center",
            isUrlActive('ordering_page_url') && "bg-primary text-primary-foreground [&_svg]:text-white [&_svg]:stroke-white",
            !isUrlAvailable('ordering_page_url') && "opacity-50 cursor-not-allowed"
          )}
        >
          <ShoppingCart className="mr-2 h-4 w-4" />
          Ordering Page
        </Button>
        
        <Button
          size="sm"
          variant={isUrlActive('review_page_url') ? "default" : "outline"}
          onClick={() => handleUrlInsert('review_page_url')}
          disabled={!isUrlAvailable('review_page_url')}
          className={cn(
            "flex items-center",
            isUrlActive('review_page_url') && "bg-primary text-primary-foreground [&_svg]:text-white [&_svg]:stroke-white",
            !isUrlAvailable('review_page_url') && "opacity-50 cursor-not-allowed"
          )}
        >
          <Star className="mr-2 h-4 w-4" />
          Review Page
        </Button>
        
        <Button
          size="sm"
          variant={isUrlActive('google_review_url') ? "default" : "outline"}
          onClick={() => handleUrlInsert('google_review_url')}
          disabled={!isUrlAvailable('google_review_url')}
          className={cn(
            "flex items-center",
            isUrlActive('google_review_url') && "bg-primary text-primary-foreground [&_svg]:text-white [&_svg]:stroke-white",
            !isUrlAvailable('google_review_url') && "opacity-50 cursor-not-allowed"
          )}
        >
          <Star className="mr-2 h-4 w-4" />
          Google Review
        </Button>
        
        <Button
          size="sm"
          variant={isUrlActive('facebook_url') ? "default" : "outline"}
          onClick={() => handleUrlInsert('facebook_url')}
          disabled={!isUrlAvailable('facebook_url')}
          className={cn(
            "flex items-center",
            isUrlActive('facebook_url') && "bg-primary text-primary-foreground [&_svg]:text-white [&_svg]:stroke-white",
            !isUrlAvailable('facebook_url') && "opacity-50 cursor-not-allowed"
          )}
        >
          <Facebook className="mr-2 h-4 w-4" />
          Facebook
        </Button>
        
        <Button
          size="sm"
          variant={isUrlActive('instagram_url') ? "default" : "outline"}
          onClick={() => handleUrlInsert('instagram_url')}
          disabled={!isUrlAvailable('instagram_url')}
          className={cn(
            "flex items-center",
            isUrlActive('instagram_url') && "bg-primary text-primary-foreground [&_svg]:text-white [&_svg]:stroke-white",
            !isUrlAvailable('instagram_url') && "opacity-50 cursor-not-allowed"
          )}
        >
          <Instagram className="mr-2 h-4 w-4" />
          Instagram
        </Button>
      </div>

      {showRestaurantSelector && (
        <p className="text-xs text-muted-foreground mt-2">
          {selectedRestaurantId === DYNAMIC_RESTAURANT_ID 
            ? "Dynamic variables will be resolved to the customer's preferred restaurant at send time"
            : "URLs will be inserted from the selected restaurant"
          }
        </p>
      )}
    </div>
  );
};

export default PersonalizationOptions;